import { isMobile } from '../utils/index.ts';

type Orientation = 'landscape' | 'portrait';
type OrientationChangeCallback = (orientation: Orientation) => void;

/**
 * 窗口控制器 - 处理窗口相关事件和横竖屏切换
 */
export class WindowController {
  private isMobile: boolean;
  private currentOrientation: Orientation;
  private orientationChangeDialog: HTMLElement | null;
  private onOrientationChangeCallback: OrientationChangeCallback | null;
  constructor() {
    this.isMobile = false;
    this.currentOrientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
    this.orientationChangeDialog = null;
    this.onOrientationChangeCallback = null;

    this.init();
  }
  init(): void {
    this.isMobile = isMobile();
  }
  /**
   * 处理窗口大小变化
   */
  onWindowResize(): void {
    console.log('window-controller', 'onWindowResize');
  }

  /**
   * 检测横竖屏变化
   */
  checkOrientationChange(): void {
    if (!this.isMobile) return;

    const newOrientation: Orientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';

    if (newOrientation !== this.currentOrientation) {
      this.currentOrientation = newOrientation;
      // 延迟显示对话框，确保屏幕旋转动画完成
      setTimeout(() => {
        this.showOrientationDialog();
      }, 500);
    }
  }

  /**
   * 创建横竖屏切换提示对话框
   */
  createOrientationDialog(): HTMLElement {
    // 创建遮罩层
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 9999;
      display: flex;
      justify-content: center;
      align-items: center;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    // 创建对话框容器
    const dialog = document.createElement('div');
    dialog.style.cssText = `
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 16px;
      padding: 24px;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
      max-width: 320px;
      width: 90%;
      text-align: center;
      transform: scale(0.7) translateY(20px);
      transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
      border: 1px solid rgba(255, 255, 255, 0.2);
    `;
    // 创建标题
    const title = document.createElement('h3');
    title.textContent = '屏幕方向已改变';
    title.style.cssText = `
      color: white;
      margin: 0 0 12px 0;
      font-size: 18px;
      font-weight: 600;
    `;
    // 创建描述文本
    const description = document.createElement('p');
    description.textContent = '为了获得最佳体验，建议重新载入页面';
    description.style.cssText = `
      color: rgba(255, 255, 255, 0.9);
      margin: 0 0 24px 0;
      font-size: 14px;
      line-height: 1.5;
    `;
    // 创建按钮容器
    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      display: flex;
      gap: 12px;
      justify-content: center;
    `;
    // 创建取消按钮
    const cancelButton = document.createElement('button');
    cancelButton.textContent = '取消';
    cancelButton.style.cssText = `
      padding: 10px 20px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      background: transparent;
      color: white;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: all 0.2s ease;
      min-width: 80px;
    `;
    // 创建确定按钮
    const confirmButton = document.createElement('button');
    confirmButton.textContent = '重新载入';
    confirmButton.style.cssText = `
      padding: 10px 20px;
      border: none;
      background: linear-gradient(45deg, #ff6b6b, #ee5a24);
      color: white;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: all 0.2s ease;
      min-width: 80px;
      box-shadow: 0 4px 12px rgba(238, 90, 36, 0.3);
    `;
    // 添加按钮悬停效果
    this.addButtonHoverEffects(cancelButton, confirmButton);
    // 添加事件监听器
    cancelButton.addEventListener('click', () => {
      this.hideOrientationDialog();
    });

    confirmButton.addEventListener('click', () => {
      window.location.reload();
    });
    // 组装对话框
    buttonContainer.appendChild(cancelButton);
    buttonContainer.appendChild(confirmButton);
    dialog.appendChild(title);
    dialog.appendChild(description);
    dialog.appendChild(buttonContainer);
    overlay.appendChild(dialog);
    // 添加到页面
    document.body.appendChild(overlay);
    // 触发动画
    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
      dialog.style.transform = 'scale(1) translateY(0)';
    });

    return overlay;
  }

  /**
   * 添加按钮悬停效果
   */
  addButtonHoverEffects(cancelButton: HTMLButtonElement, confirmButton: HTMLButtonElement): void {
    cancelButton.addEventListener('mouseenter', () => {
      cancelButton.style.background = 'rgba(255, 255, 255, 0.1)';
      cancelButton.style.transform = 'translateY(-1px)';
    });

    cancelButton.addEventListener('mouseleave', () => {
      cancelButton.style.background = 'transparent';
      cancelButton.style.transform = 'translateY(0)';
    });

    confirmButton.addEventListener('mouseenter', () => {
      confirmButton.style.transform = 'translateY(-1px) scale(1.02)';
      confirmButton.style.boxShadow = '0 6px 16px rgba(238, 90, 36, 0.4)';
    });

    confirmButton.addEventListener('mouseleave', () => {
      confirmButton.style.transform = 'translateY(0) scale(1)';
      confirmButton.style.boxShadow = '0 4px 12px rgba(238, 90, 36, 0.3)';
    });
  }
  /**
   * 显示横竖屏切换对话框
   */
  showOrientationDialog(): void {
    if (this.orientationChangeDialog) {
      this.hideOrientationDialog();
    }
    this.orientationChangeDialog = this.createOrientationDialog();
  }

  /**
   * 隐藏横竖屏切换对话框
   */
  hideOrientationDialog(): void {
    if (this.orientationChangeDialog) {
      const dialog = this.orientationChangeDialog.querySelector('div');
      this.orientationChangeDialog.style.opacity = '0';
      (dialog as HTMLDivElement).style.transform = 'scale(0.7) translateY(20px)';

      setTimeout(() => {
        if (this.orientationChangeDialog?.parentNode) {
          this.orientationChangeDialog.parentNode.removeChild(this.orientationChangeDialog);
        }
        this.orientationChangeDialog = null;
      }, 300);
    }
  }

  /**
   * 设置方向变化回调函数
   */
  setOrientationChangeCallback(callback: OrientationChangeCallback): void {
    this.onOrientationChangeCallback = callback;
  }

  /**
   * 获取是否为移动设备
   */
  getIsMobile(): boolean {
    return this.isMobile;
  }

  /**
   * 获取当前屏幕方向
   */
  getCurrentOrientation(): Orientation {
    return this.currentOrientation;
  }

  /**
   * 销毁控制器
   */
  destroy(): void {
    // window.removeEventListener('resize', this.handleResize?.bind(this));
    this.hideOrientationDialog();
  }
}
